const StorageKeys = {
  LAST_PAYMENT_METHOD: 'LAST_PAYMENT_METHOD',
  IS_TG: 'IS_TG',
  TG_LANGUAGE: 'TG_LANGUAGE',
  IS_ONBOARDING_ENABLED: 'IS_ONBOARDING_ENABLED',
  SEEN_SEASON_TOURNAMENT_IDS: 'SEEN_SEASON_TOURNAMENT_IDS',
  SEEN_BATTLE_IDS: 'SEEN_BATTLE_IDS',
  SEEN_TASK_CAMPAIGN_IDS: 'SEEN_TASK_CAMPAIGN_IDS',
  SEEN_SINGLE_TOURNAMENT_IDS: 'SEEN_SINGLE_TOURNAMENT_IDS',
  SEEN_NFT_TOURNAMENT_IDS: 'SEEN_NFT_TOURNAMENT_IDS',
} as const;

export type StorageKeysType = typeof StorageKeys[keyof typeof StorageKeys];

type SeenItem = {
  id: string;
  addedAt: number;
};

const MAX_SEEN_IDS = 50;

// Имя кастомного события для синхронизации изменений localStorage в той же вкладке
const LOCAL_STORAGE_CHANGE_EVENT = 'localStorageChange';

class StorageUtility {
  /**
   * Диспатчит кастомное событие об изменении localStorage
   * Это необходимо для реактивности в той же вкладке, т.к. нативное storage event
   * срабатывает только в других вкладках
   */
  private static dispatchStorageChangeEvent(key: StorageKeysType): void {
    const event = new CustomEvent(LOCAL_STORAGE_CHANGE_EVENT, {
      detail: { key },
    });
    window.dispatchEvent(event);
  }

  static setItem<T>(key: StorageKeysType, value: T): void {
    try {
      const jsonValue = JSON.stringify(value);
      localStorage.setItem(key, jsonValue);
      this.dispatchStorageChangeEvent(key);
    } catch (e) {}
  }

  static getItem<T>(key: StorageKeysType): T | null {
    try {
      const jsonValue = localStorage.getItem(key);
      const value = jsonValue != null ? JSON.parse(jsonValue) : null;
      return value;
    } catch (e) {
      return null;
    }
  }

  static removeItem(key: StorageKeysType): void {
    try {
      localStorage.removeItem(key);
      this.dispatchStorageChangeEvent(key);
    } catch (e) {}
  }

  static clear(): void {
    try {
      localStorage.clear();
      // При clear диспатчим событие без ключа (аналогично нативному storage event)
      const event = new CustomEvent(LOCAL_STORAGE_CHANGE_EVENT, {
        detail: { key: null },
      });
      window.dispatchEvent(event);
    } catch (error) {}
  }

  static getSeenIds(key: StorageKeysType): string[] {
    const items = this.getItem<SeenItem[]>(key);
    if (!items || !Array.isArray(items)) {
      return [];
    }
    return items.map((item) => item.id);
  }

  static addSeenIds(key: StorageKeysType, newIds: string[]): void {
    const existingItems = this.getItem<SeenItem[]>(key) ?? [];
    const existingIdsSet = new Set(existingItems.map((item) => item.id));
    const now = Date.now();

    // Добавляем только новые ID с текущей датой
    const newItems: SeenItem[] = newIds.filter((id) => !existingIdsSet.has(id)).map((id) => ({ id, addedAt: now }));

    // Объединяем существующие и новые
    const allItems = [...existingItems, ...newItems];

    // Сортируем по дате (новые первые)
    allItems.sort((a, b) => b.addedAt - a.addedAt);

    // Оставляем только первые MAX_SEEN_IDS записей
    const limitedItems = allItems.slice(0, MAX_SEEN_IDS);

    this.setItem(key, limitedItems);
  }

  static clearSeenIds(key: StorageKeysType): void {
    this.removeItem(key);
  }
}

export { StorageUtility, StorageKeys, LOCAL_STORAGE_CHANGE_EVENT };
